'use client';
import { TFunction } from 'i18next';
import { TimeUnit } from '../components/actions/ModalActions';
import { BASE_PATH } from '../components/language/i18n';
import { getDateFromSeconds } from './dateHelper';

export const isPublicShare = (): boolean => {
  if (typeof window === 'undefined') return false;
  const targetPath = window?.location?.hash
    ? window?.location?.hash?.replace('#/', '')
    : window?.location?.pathname || '';
  console.log('🚀 ~ isPublicShare ~ targetPath:', targetPath);
  const isShare =
    targetPath?.startsWith('smartshare/') ||
    targetPath?.startsWith(BASE_PATH + '/smartshare');
  console.log('🚀 ~ isPublicShare ~ isShare:', isShare);
  return isShare;
};

export const isPublicDownload = (): boolean => {
  if (typeof window === 'undefined') return false;
  const targetPath = window?.location?.hash
    ? window?.location?.hash?.replace('#/', '')
    : window?.location?.pathname || '';
  console.log('🚀 ~ isPublicDownload ~ targetPath:', targetPath);
  const isDownload =
    targetPath.startsWith('public/') ||
    targetPath.startsWith('download/') ||
    targetPath.startsWith(BASE_PATH + '/download') ||
    targetPath.startsWith(BASE_PATH + '/public');
  console.log('🚀 ~ isPublicDownload ~ isDownload:', isDownload);
  return isDownload;
};

export const hasHtmlTags = (text: string): boolean => {
  const regex = /<\/?[^>]+(\/?)>/;
  return regex.test(text);
};

export const isPublicOrDownload = (): boolean => {
  return isPublicShare() || isPublicDownload();
};

export type AnyFunction = (...args: any[]) => any;

type IsPromise<T> = T extends Promise<any> ? true : false;

type ReturnsPromise<T extends AnyFunction> = IsPromise<ReturnType<T>>;

export function isAsync<T extends AnyFunction>(func: T): ReturnsPromise<T> {
  const isAsync = func.constructor.name === 'AsyncFunction';
  const hasPromisePrototype = func.prototype?.then instanceof Function;

  if (isAsync || hasPromisePrototype) {
    return true as ReturnsPromise<T>;
  }

  try {
    const result = func();
    return (result instanceof Promise) as ReturnsPromise<T>;
  } catch {
    return false as ReturnsPromise<T>;
  }
}

/** Durata in secondi di una singola unità di tempo. */
const TIME_UNIT_SECONDS: Record<TimeUnit, number> = {
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
  weeks: 60 * 60 * 24 * 7,
};

/** Chiave di traduzione al singolare per ogni unità di tempo. */
const TIME_UNIT_SINGULAR: Record<TimeUnit, string> = {
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
  weeks: 'week',
};

export const getDurationInSeconds = (value: number, timeUnit: TimeUnit) => {
  return value * (TIME_UNIT_SECONDS[timeUnit] ?? TIME_UNIT_SECONDS.minutes);
};

export const getDurationFromSeconds = (
  seconds: number,
  t: TFunction,
  timeUnit?: TimeUnit,
): { value: number; unit: string; expiresOnDate: string } => {
  let unit = 'hours';
  let value = 0;
  let expiresOnDate = '';
  if (timeUnit) {
    // mantiene l'unità scelta dall'utente invece di normalizzarla
    value = Math.floor(seconds / TIME_UNIT_SECONDS[timeUnit]);
    unit = value === 1 ? TIME_UNIT_SINGULAR[timeUnit] : timeUnit;
  } else if (seconds < 3600) {
    // meno di un'ora
    value = Math.floor(seconds / 60);
    unit = value === 1 ? 'minute' : 'minutes';
  } else if (seconds < 86400) {
    // meno di un giorno
    value = Math.floor(seconds / 3600);
    unit = value === 1 ? 'hour' : 'hours';
  } else if (seconds < 604800) {
    // meno di una settimana
    value = Math.floor(seconds / 86400);
    unit = value === 1 ? 'day' : 'days';
  } else {
    value = Math.floor(seconds / 604800);
    unit = value === 1 ? 'week' : 'weeks';
  }
  const unitTranslation = t(`globals.${unit}`);
  expiresOnDate = getDateFromSeconds(seconds);
  return { value, unit: unitTranslation, expiresOnDate };
};
